<template>
  <header class="kactus-header">
    <!-- Top Bar -->
    <div class="top-announcement text-wrap py-2 text-center">
      <p class="mb-2">{{ currentLang.topWelcome }}</p>

      <p class="m-0 small">
        {{ currentLang.topDescription }}
      </p>
    </div>

    <!-- Social & Slogan Bar -->
    <div class="container-fluid py-3">
      <div class="container d-flex justify-content-between align-items-center">
        <div class="lang-switch">
          <button
            type="button"
            class="btn btn-sm lang-btn"
            :class="{ active: lang === 'fa' }"
            @click="changeLanguage('fa')"
          >
            {{ currentLang.languageFa }}
          </button>

          <button
            type="button"
            class="btn btn-sm lang-btn"
            :class="{ active: lang === 'en' }"
            @click="changeLanguage('en')"
          >
            {{ currentLang.languageEn }}
          </button>
        </div>
        <div class="social-icons d-flex gap-3">
          <a
            href="https://t.me/zabankid"
            target="_blank"
            rel="nofollow noopener noreferrer"
            aria-label="Telegram"
          >
            <i class="bi bi-telegram fs-5"></i
          ></a>

          <a
            href="https://www.instagram.com/zaban.kid?igsh=cG04NXJ5Mmgyd3B1"
            target="_blank"
            rel="nofollow noopener noreferrer"
            aria-label="Telegram"
          >
            <i class="bi bi-instagram fs-5"></i
          ></a>
          <a
            href="https://www.aparat.com/zabankid_education"
            target="_blank"
            rel="nofollow noopener noreferrer"
            aria-label="Aparat"
          >
            <i class="bi bi-play-circle fs-5"></i>
          </a>

          <a href="#"><i class="bi bi-youtube fs-5"></i></a>
        </div>
      </div>
    </div>

    <!-- Main White Navigation Bar -->
    <div
      class="main-nav-wrapper d-none d-lg-block"
      :class="{ 'is-fixed': isSticky }"
    >
      <div class="container-fluid position-relative">
        <div
          class="nav-white-card d-flex align-items-center justify-content-between px-4 shadow-sm"
        >
          <!-- Right Section: Login & Contact -->
          <div
            class="login-and-phone d-flex align-items-center gap-4 d-none d-lg-flex section-side"
          >
            <div
              v-if="!isLoggedIn"
              class="auth-link background-button-login d-flex align-items-center gap-2"
              role="button"
              tabindex="0"
              @click="showAuthModal = true"
              @keydown.enter="showAuthModal = true"
            >
              <i class="bi bi-person fs-4"></i>
              <span>{{ currentLang.loginRegister }}</span>
            </div>

            <!-- زمانی که کاربر وارد شده است -->
            <div v-else class="user-menu-wrapper">
              <button
                type="button"
                class="background-button-login d-flex align-items-center gap-2"
                :class="{ active: showUserMenu }"
                aria-label="منوی حساب کاربری"
                :aria-expanded="showUserMenu"
                @click.stop="toggleUserMenu"
              >
                <span>
                  <i class="user-avatar bi bi-person-fill"></i>
                </span>

                <i
                  class="bi bi-chevron-down user-chevron"
                  :class="{ rotate: showUserMenu }"
                ></i>
              </button>

              <Transition name="user-dropdown">
                <div v-if="showUserMenu" class="user-dropdown">
                  <!-- شماره موبایل -->
                  <div class="user-mobile-info">
                    <span class="user-mobile-label">
                      <i class="bi bi-phone"></i>
                      {{ currentLang.mobileNumber }}
                    </span>
                    <router-link
                      :to="currentLang.urls.Profile"
                      class="user-dropdown-item text-decoration-none"
                    >
                      <strong dir="ltr">{{ mobileNumber }}</strong>
                    </router-link>
                  </div>

                  <div class="dropdown-line"></div>

                  <!-- سفارشات -->
                  <!-- <button
                    type="button"
                    class="user-dropdown-item"
                    @click="goToOrders"
                  >
                    <span>
                      <i class="bi bi-bag-check"></i>
                      {{ currentLang.orderList }}
                    </span>

                    <i class="bi bi-chevron-left"></i>
                  </button> -->

                  <router-link
                    :to="currentLang.urls.Order"
                    class="user-dropdown-item text-decoration-none"
                  >
                    <span>
                      <i class="bi bi-bag-check"></i>
                      {{ currentLang.orderList }}
                    </span>

                    <i class="bi bi-chevron-left"></i>
                  </router-link>

                  <div class="dropdown-line"></div>

                  <!-- خروج -->
                  <button
                    type="button"
                    class="user-dropdown-item logout-item"
                    @click="logout"
                  >
                    <span>
                      <i class="bi bi-box-arrow-left"></i>
                      {{ currentLang.logout }}
                    </span>
                  </button>
                </div>
              </Transition>
            </div>

            <div
              class="contact-info d-flex align-items-center gap-2 border-end pe-4"
            >
              <span class="fw-bold">{{ currentLang.phoneNumber }}</span>
              <i class="bi bi-telephone fs-5"></i>
            </div>
          </div>

          <!-- Center Logo -->
          <div class="logo-container">
            <div class="logo-circle">
              <img
                src="/logo.png"
                class="logo-img"
                :alt="currentLang.logoAlt"
              />
            </div>
          </div>

          <!-- Left Section: Search & Cart -->
          <div
            class="d-flex align-items-center gap-3 section-side justify-content-end"
          >
            <div class="search-icon d-none d-sm-block">
              <i class="bi bi-search fs-5"></i>
            </div>
            <div
              class="wishlist-compare d-none d-sm-flex gap-2 border-start ps-3"
            >
              <i class="bi bi-heart fs-5 position-relative">
                <span class="badge-count">۰</span>
              </i>
              <i class="bi bi-shuffle fs-5 position-relative">
                <span class="badge-count">۰</span>
              </i>
            </div>
            <a class="text-decoration-none" :href="`${currentLang.urls.Cart}`">
              <div
                class="cart-button d-flex align-items-center gap-2 p-1 px-3 rounded-pill"
              >
                <span class="small fw-bold"
                  >{{ totalPrice }} {{ currentLang.cartPrice }}</span
                >
                <div class="cart-icon-bg shadow-sm">
                  <i class="cart-icon bi bi-cart"></i>
                </div>
              </div>
            </a>
          </div>
        </div>

        <nav class="nav-menu bottom-menu">
          <div
            class="container d-flex justify-content-between align-items-center flex-wrap"
          >
            <!-- منوی سمت راست -->
            <ul class="nav gap-lg-4 gap-2 list-unstyled mb-0">
              <li>
                <router-link
                  :to="currentLang.urls.Main"
                  class="nav-link text-white"
                >
                  {{ currentLang.home }}
                </router-link>
              </li>
              <li
                class="book-categories-menu"
                @mouseenter="openCategoriesMenu"
                @mouseleave="closeCategoriesMenu"
              >
                <a
                  href="#"
                  class="nav-link text-white"
                  :class="{ 'menu-is-open': isCategoriesMenuOpen }"
                  @click.prevent="toggleCategoriesMenu"
                >
                  {{ currentLang.bookCategories }}

                  <i
                    class="bi bi-chevron-down small categories-chevron"
                    :class="{ rotated: isCategoriesMenuOpen }"
                  ></i>
                </a>

                <Transition name="categories-dropdown">
                  <div v-if="isCategoriesMenuOpen" class="categories-mega-menu">
                    <div
                      v-if="categoriesLoading"
                      class="categories-status d-flex align-items-center justify-content-center"
                    >
                      <div
                        class="spinner-border spinner-border-sm text-info"
                        role="status"
                      ></div>
                    </div>

                    <div
                      v-else-if="categoriesError"
                      class="categories-status text-danger"
                    >
                      {{ categoriesError }}
                    </div>

                    <div v-else class="categories-columns">
                      <!-- سطح اول -->
                      <div class="categories-column">
                        <div class="categories-column-title">
                          {{ currentLang.bookCategories }}
                        </div>

                        <button
                          v-for="category in rootCategories"
                          :key="category.id"
                          type="button"
                          class="category-menu-item"
                          :class="{
                            active: activeFirstCategory?.id === category.id,
                          }"
                          @mouseenter="selectFirstCategory(category)"
                          @click="selectFirstCategory(category)"
                        >
                          <span>{{ category.title }}</span>

                          <i
                            v-if="hasChildren(category)"
                            class="bi bi-chevron-left category-menu-arrow"
                          ></i>
                        </button>
                      </div>

                      <!-- سطح دوم -->
                      <div class="categories-column">
                        <div class="categories-column-title">
                          {{ activeFirstCategory?.title || "زیر دسته‌ها" }}
                        </div>

                        <template v-if="activeFirstCategory?.children?.length">
                          <button
                            v-for="category in activeFirstCategory.children"
                            :key="category.id"
                            type="button"
                            class="category-menu-item"
                            :class="{
                              active: activeSecondCategory?.id === category.id,
                            }"
                            @mouseenter="selectSecondCategory(category)"
                            @click="selectSecondCategory(category)"
                          >
                            <span>{{ category.title }}</span>

                            <i
                              v-if="hasChildren(category)"
                              class="bi bi-chevron-left category-menu-arrow"
                            ></i>
                          </button>
                        </template>

                        <p v-else class="category-empty-state">
                          دسته‌ای را انتخاب کنید
                        </p>
                      </div>

                      <!-- سطح سوم -->
                      <div class="categories-column">
                        <div class="categories-column-title">
                          {{ activeSecondCategory?.title || "کتاب‌ها" }}
                        </div>

                        <template v-if="activeSecondCategory?.children?.length">
                          <router-link
                            v-for="category in activeSecondCategory.children"
                            :key="category.id"
                            :to="`${currentLang.urls.ProductCategory}/${category.slug}`"
                            class="category-menu-item category-last-level"
                            @click="selectFinalCategory(category)"
                          >
                            <span>{{ category.title }}</span>
                            <i
                              class="bi bi-arrow-up-left category-menu-arrow"
                            ></i>
                          </router-link>
                        </template>

                        <p v-else class="category-empty-state">
                          زیر دسته‌ای وجود ندارد
                        </p>
                      </div>
                    </div>
                  </div>
                </Transition>
              </li>

              <li>
                <router-link
                  :to="currentLang.urls.AboutUs"
                  class="nav-link text-white"
                >
                  {{ currentLang.aboutUs }}
                </router-link>
              </li>
            </ul>

            <!-- منوی سمت چپ -->
            <ul class="nav gap-lg-4 gap-2 list-unstyled mb-0">
              <li>
                <a href="#" class="nav-link text-white">
                  {{ currentLang.rulesAndRegulations }}</a
                >
              </li>
              <li>
                <router-link
                  :to="currentLang.urls.FAQ"
                  class="nav-link text-white"
                >
                  {{ currentLang.FAQ }}
                </router-link>
              </li>
              <li>
                <router-link
                  :to="currentLang.urls.Contact"
                  class="nav-link text-white"
                >
                  {{ currentLang.contactUs }}
                </router-link>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </div>

    <!-- فقط موبایل و تبلت؛ از lg به بالا مخفی -->
    <div
      class="mobile-header d-lg-none"
      :class="{ 'is-sticky-mobile': isStickyMobile }"
    >
      <div class="container">
        <div class="mobile-header__content">
          <!-- سمت راست: منو و جستجو -->
          <div class="header-actions header-actions--right">
            <button
              type="button"
              class="header-icon-btn"
              aria-label="باز کردن منو"
              @click="isMenuOpen = true"
            >
              <i class="bi bi-list"></i>
            </button>

            <button
              type="button"
              class="header-icon-btn"
              aria-label="جستجو"
              @click="handleSearch"
            >
              <i class="bi bi-search"></i>
            </button>
          </div>

          <!-- وسط: لوگو -->
          <RouterLink to="/" class="mobile-logo" aria-label="صفحه اصلی">
            <!-- بعداً مسیر لوگو را تغییر بده -->
            <img src="/logo.png" alt="لوگو سایت" />
          </RouterLink>

          <!-- سمت چپ: ورود و سبد خرید -->
          <div class="header-actions header-actions--left">
            <div
              v-if="!isLoggedIn"
              role="button"
              tabindex="0"
              @click="showAuthModal = true"
              @keydown.enter="showAuthModal = true"
              to="/login"
              class="header-icon-btn"
              aria-label="ورود یا ثبت‌نام"
            >
              <i class="bi bi-person"></i>
            </div>

            <RouterLink
              v-if="isLoggedIn"
              to="/cart"
              class="header-icon-btn"
              aria-label="کاربر"
            >
              <i class="bi bi-person"></i>
            </RouterLink>

            <RouterLink
              :to="`${currentLang.urls.Cart}`"
              class="header-icon-btn position-relative"
              aria-label="سبد خرید"
            >
              <i class="bi bi-cart">
                <span class="badge-count">{{ totalCount }}</span>
              </i>
            </RouterLink>
          </div>
        </div>
      </div>
    </div>

    <!-- لایه تیره پشت منو -->
    <Transition name="fade">
      <div
        v-if="isMenuOpen"
        class="menu-backdrop"
        @click="isMenuOpen = false"
      ></div>
    </Transition>

    <!-- منوی کشویی از سمت راست -->
    <Transition name="slide-right">
      <aside v-if="isMenuOpen" class="mobile-sidebar">
        <div class="mobile-sidebar__header">
          <span class="fw-bold">{{ currentLang.titleMenuInMobile }}</span>

          <button
            type="button"
            class="header-icon-btn"
            aria-label="بستن منو"
            @click="isMenuOpen = false"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <nav class="mobile-sidebar__nav">
          <router-link :to="currentLang.urls.Main" @click="isMenuOpen = false">
            <i class="bi bi-house"></i>
            {{ currentLang.home }}
          </router-link>

          <div class="mobile-category-item">
            <!-- لینک باز و بسته کردن دسته‌بندی با همان ساختار سایر لینک‌ها -->
            <a
              href="#"
              class="mobile-menu-link"
              :class="{ 'menu-item-active': mobileCategoriesOpen }"
              @click.prevent="toggleMobileCategories"
            >
              <div class="d-flex align-items-center gap-2">
                <i class="bi bi-grid"></i>
                <!-- یا bi-book-half یا هر آیکونی که مد نظرتونه -->
                <span>{{ currentLang.bookCategories }}</span>
              </div>

              <!-- فلش باز و بسته شدن در انتهای سمت دیگر -->
              <i
                class="bi bi-chevron-down mobile-arrow small"
                :class="{ rotated: mobileCategoriesOpen }"
              ></i>
            </a>

            <!-- زیرمنوهای آکاردئونی (سطح ۱، ۲ و ۳) -->
            <Transition name="accordion">
              <div v-if="mobileCategoriesOpen" class="mobile-sub-menu">
                <!-- لودینگ -->
                <div
                  v-if="categoriesLoading"
                  class="mobile-menu-status d-flex justify-content-center py-2"
                >
                  <div
                    class="spinner-border spinner-border-sm text-info"
                    role="status"
                  ></div>
                </div>

                <!-- ارور -->
                <div
                  v-else-if="categoriesError"
                  class="mobile-menu-status text-danger py-2 px-3"
                >
                  {{ categoriesError }}
                </div>

                <!-- لیست دسته‌ها -->
                <div v-else class="mobile-categories-tree">
                  <div
                    v-for="cat1 in rootCategories"
                    :key="cat1.id"
                    class="mobile-tree-item"
                  >
                    <!-- دکمه سطح ۱ -->
                    <button
                      type="button"
                      class="mobile-item-btn level-1-btn"
                      :class="{ active: expandedLevel1 === cat1.id }"
                      @click="toggleLevel1(cat1)"
                    >
                      <span>{{ cat1.title }}</span>
                      <i
                        v-if="hasChildren(cat1)"
                        class="bi bi-chevron-down mobile-arrow small"
                        :class="{ rotated: expandedLevel1 === cat1.id }"
                      ></i>
                    </button>

                    <!-- زیرمنوی سطح ۲ -->
                    <Transition name="accordion">
                      <div
                        v-if="
                          expandedLevel1 === cat1.id && cat1.children?.length
                        "
                        class="mobile-level-2-list"
                      >
                        <div
                          v-for="cat2 in cat1.children"
                          :key="cat2.id"
                          class="mobile-tree-item"
                        >
                          <!-- دکمه سطح ۲ -->
                          <button
                            type="button"
                            class="mobile-item-btn level-2-btn"
                            :class="{ active: expandedLevel2 === cat2.id }"
                            @click="toggleLevel2(cat2)"
                          >
                            <span>{{ cat2.title }}</span>
                            <i
                              v-if="hasChildren(cat2)"
                              class="bi bi-chevron-down mobile-arrow small"
                              :class="{ rotated: expandedLevel2 === cat2.id }"
                            ></i>
                          </button>

                          <!-- زیرمنوی سطح ۳ (لینک نهایی) -->
                          <Transition name="accordion">
                            <div
                              v-if="
                                expandedLevel2 === cat2.id &&
                                cat2.children?.length
                              "
                              class="mobile-level-3-list"
                            >
                              <router-link
                                v-for="cat3 in cat2.children"
                                :key="cat3.id"
                                :to="`${currentLang.urls.ProductCategory}/${cat3.slug}`"
                                class="mobile-item-btn level-3-btn"
                                @click="
                                  selectFinalCategory(cat3);
                                  isMenuOpen = false;
                                "
                              >
                                <span>{{ cat3.title }}</span>
                                <i
                                  class="bi bi-arrow-left mobile-action-icon"
                                ></i>
                              </router-link>
                            </div>
                          </Transition>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <RouterLink to="/products" @click="isMenuOpen = false">
            <i class="bi bi-shop"></i>
            محصولات
          </RouterLink>

          <router-link :to="currentLang.urls.FAQ" @click="isMenuOpen = false">
            <i class="bi bi-telephone"></i>

            {{ currentLang.FAQ }}
          </router-link>

          <router-link
            :to="currentLang.urls.AboutUs"
            @click="isMenuOpen = false"
          >
            <i class="bi bi-info-circle"></i>
            {{ currentLang.aboutUs }}
          </router-link>

          <router-link
            :to="currentLang.urls.Contact"
            @click="isMenuOpen = false"
          >
            <i class="bi bi-telephone"></i>
            {{ currentLang.contactUs }}
          </router-link>
        </nav>
      </aside>
    </Transition>

    <!-- Bottom Menu -->
  </header>
  <AuthModal v-model="showAuthModal" @success="handleLoginSuccess" />
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, onBeforeUnmount } from "vue";
import api from "@/plugins/axios";
import { useRoute, useRouter } from "vue-router";
import AuthModal from "@/components/AuthModal.vue";
import { lang, setLang } from "@/plugins/lang";
import enAuth from "@/locales/en/header";
import faAuth from "@/locales/fa/header";
import { useCart } from "@/stores/cart";
const { totalPrice, totalCount } = useCart();

const route = useRoute();
const router = useRouter();

const showAuthModal = ref(false);

const translations = {
  en: enAuth,
  fa: faAuth,
};
const currentLang = computed(() => translations[lang.value]);

const showUserMenu = ref(false);

const authToken = ref(localStorage.getItem("auth_token"));
const userInfo = ref(null);

try {
  const savedUser = localStorage.getItem("user_info");
  userInfo.value = savedUser ? JSON.parse(savedUser) : null;
} catch (error) {
  localStorage.removeItem("user_info");
}

const isMenuOpen = ref(false);

// بعداً می‌توانی این مقدار را از Pinia یا props بگیری
// const cartCount = ref(0)

const handleSearch = () => {
  // اینجا می‌توانی مودال جستجو باز کنی
  // یا کاربر را به صفحه جستجو ببری:
  // router.push('/search')

  console.log("Search clicked");
};

const isStickyMobile = ref(false);

const handleScrollMobile = () => {
  isStickyMobile.value = window.scrollY > 12;
};

const isLoggedIn = computed(() => {
  return Boolean(authToken.value && userInfo.value);
});

const mobileNumber = computed(() => {
  return userInfo.value?.mobile || userInfo.value?.phone || "";
});

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value;
}

function closeUserMenu(event) {
  if (!event.target.closest(".user-menu-wrapper")) {
    showUserMenu.value = false;
  }
}

// function goToOrders() {
//   showUserMenu.value = false;

//   // اگر Vue Router داری:
//   // router.push('/orders')
// }

function logout() {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user_info");

  authToken.value = null;
  userInfo.value = null;
  showUserMenu.value = false;

  // اگر Vue Router داری:
  // router.push('/')
}
function syncAuthData() {
  authToken.value = localStorage.getItem("auth_token") || "";

  try {
    const savedUser = localStorage.getItem("user_info");
    userInfo.value = savedUser ? JSON.parse(savedUser) : null;
  } catch (error) {
    console.error("خطا در خواندن اطلاعات کاربر:", error);

    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_info");

    authToken.value = "";
    userInfo.value = null;
  }
}

function handleLoginSuccess() {
  syncAuthData();
}

const isSticky = ref(false);

const handleScroll = () => {
  isSticky.value = window.scrollY > 120;
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("scroll", handleScrollMobile);
  document.addEventListener("click", closeUserMenu);
  syncAuthData();
  getCategories();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("scroll", handleScrollMobile);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", closeUserMenu);
});

const isCategoriesMenuOpen = ref(false);
const categoriesLoading = ref(false);
const categoriesError = ref("");
const categories = ref([]);

const activeFirstCategory = ref(null);
const activeSecondCategory = ref(null);

const rootCategories = computed(() => {
  return categories.value.filter((category) => category.parent_id === null);
});

const hasChildren = (category) => {
  return Array.isArray(category?.children) && category.children.length > 0;
};

const getCategories = async () => {
  categoriesLoading.value = true;
  categoriesError.value = "";

  try {
    const response = await api.get("/general/categories");

    if (!response.status === 200) {
      throw new Error("دریافت دسته‌بندی‌ها ناموفق بود.");
    }

    const result = response.data;

    categories.value = Array.isArray(result?.data) ? result.data : [];

    if (rootCategories.value.length) {
      selectFirstCategory(rootCategories.value[0]);
    }
  } catch (error) {
    categoriesError.value =
      error.message || "خطایی در دریافت دسته‌بندی‌ها رخ داد.";
  } finally {
    categoriesLoading.value = false;
  }
};

const selectFirstCategory = (category) => {
  activeFirstCategory.value = category;

  activeSecondCategory.value = hasChildren(category)
    ? category.children[0]
    : null;
};

const selectSecondCategory = (category) => {
  activeSecondCategory.value = category;
};

const openCategoriesMenu = () => {
  if (window.innerWidth >= 992) {
    isCategoriesMenuOpen.value = true;
  }
};

const closeCategoriesMenu = () => {
  if (window.innerWidth >= 992) {
    isCategoriesMenuOpen.value = false;
  }
};

const toggleCategoriesMenu = () => {
  if (window.innerWidth < 992) {
    isCategoriesMenuOpen.value = !isCategoriesMenuOpen.value;
  }
};

const selectFinalCategory = (category) => {
  isCategoriesMenuOpen.value = false;

  console.log("دسته‌بندی انتخاب‌شده:", category);

  // نمونه برای رفتن به صفحه محصولات:
  // window.location.href = `/products?category=${category.id}`
};

const changeLanguage = (targetLang) => {
  if (lang.value === targetLang) return;

  // ۱. پیدا کردن نام مسیر متناظر در زبان دیگر
  const targetRouteName = route.meta?.alternateName;

  if (targetRouteName) {
    // اگر صفحه داینامیک است (مثلاً محصول)
    // فرض: اسلاگ معادل در state روتر یا query یا بک‌اند ذخیره شده است
    const targetParams = { ...route.params };

    // اگر صفحه محصول بود و اسلاگ زبان دیگر را در route.meta یا کامپوننت داشتیم
    if (route.params.slug && route.meta.alternateSlug) {
      targetParams.slug = route.meta.alternateSlug;
    }

    router.push({
      name: targetRouteName,
      params: targetParams,
      query: route.query,
    });
  } else {
    // در صورت نبود تعریف، به صفحه اصلی آن زبان برود
    router.push(targetLang === "fa" ? "/" : "/en");
  }

  // ۲. آپدیت استیت محلی
  setLang(targetLang);
};

// متغیرهای وضعیت باز بودن سطوح در موبایل
const mobileCategoriesOpen = ref(false);
const expandedLevel1 = ref(null); // آی‌دی دسته سطح اول
const expandedLevel2 = ref(null); // آی‌دی دسته سطح دوم

// باز و بسته کردن دکمه اصلی منو در موبایل
const toggleMobileCategories = () => {
  mobileCategoriesOpen.value = !mobileCategoriesOpen.value;
};

// باز و بسته کردن سطح اول
const toggleLevel1 = (cat) => {
  if (hasChildren(cat)) {
    expandedLevel1.value = expandedLevel1.value === cat.id ? null : cat.id;
    expandedLevel2.value = null; // بستن سطح ۲ هنگام تعویض سطح ۱
  } else {
    selectFinalCategory(cat);
  }
};

// باز و بسته کردن سطح دوم
const toggleLevel2 = (cat) => {
  if (hasChildren(cat)) {
    expandedLevel2.value = expandedLevel2.value === cat.id ? null : cat.id;
  } else {
    selectFinalCategory(cat);
  }
};
</script>


<style scoped>
.login-and-phone {
  color: #18324b;
}

.top-announcement {
  opacity: 1;
  background-color: #00cdc4;
  color: #fff;
}

.social-icons a {
  color: #00cdc4;
  transition: 0.3s;
}

.social-icons a:hover {
  color: #1b8b86;
}

/* بخش اصلی ناوبری */

.nav-white-card {
  background: white;
  border-radius: 50px;
  height: 80px;
  position: relative;
  z-index: 10;
  transition: border-radius 0.3s ease, margin 0.3s ease; /* انیمیشن نرم */
}

/* ۲. حذف فاصله داخلی کانتینر در حالت فیکس تا لبه به لبه صفحه شود */
.main-nav-wrapper.is-fixed .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* ۳. استایل کارت سفید در حالت فیکس: تمام‌عرض و گوشه‌های تیز */
.main-nav-wrapper.is-fixed .nav-white-card {
  border-radius: 0 !important;
  width: 100%;
}
.main-nav-wrapper.is-fixed .nav-menu {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.main-nav-wrapper.is-fixed .logo-circle {
  top: -60px; /* اضافه کردن px */
}

.nav-menu {
  margin-left: 5%;
  margin-right: 5%;
  padding-bottom: 20px;
  background: white;
  margin-top: -1%;
  /* 0 بالا-چپ، 0 بالا-راست، 50px پایین-راست، 50px پایین-چپ */
  border-radius: 0 0 50px 50px;
  position: relative;
  z-index: 8;
}

.section-side {
  flex: 1;
}

.main-nav-wrapper {
  transition: all 0.3s ease;
  width: 100%;
}

/* وقتی اسکرول شد این استایل فعال می‌شود */
.main-nav-wrapper.is-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1050;
  animation: slideDown 0.35s ease;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
}

.nav-menu .nav-link {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;

  font-size: 0.9rem;
  font-weight: 600;

  color: #ffffff !important;
  opacity: 1 !important;

  padding: 9px 16px;
  text-decoration: none !important;
  transition: color 0.3s ease;
}

/* بک‌گراند متوازی‌الاضلاع */
.nav-menu .nav-link::before {
  content: "";
  position: absolute;
  inset: 2px -4px;
  z-index: -1;

  background: linear-gradient(135deg, #00ddd3, #00b8b0);

  transform: skewX(-14deg) scaleX(0.85);
  transform-origin: center;

  opacity: 0;
  border-radius: 6px;
  box-shadow: 0 7px 16px rgba(0, 205, 196, 0.3),
    0 3px 7px rgba(0, 120, 140, 0.14);

  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* نمایش شکل در هاور */
.nav-menu .nav-link:hover::before {
  opacity: 1;
  transform: skewX(-14deg) scaleX(1);
}

/* فلش */
.nav-menu .nav-link i {
  display: inline-block;
  transition: transform 0.3s ease;
}

.nav-menu .nav-link:hover i {
  transform: rotate(180deg);
}

/* استایل لوگوی دایره‌ای وسط */
.logo-container {
  background: #ffffff;
  position: relative;
  width: 350px;
  display: flex;
  justify-content: center;
  border: 12px solid #ffffff;
  z-index: 5;
}

.logo-circle {
  background: #fff8ed;
  width: 190px;
  height: 190px;
  border-radius: 50%;
  position: absolute;
  top: -80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 12px solid #ffffff;
  box-shadow: 0 25px 50px -20px rgba(0, 0, 0, 0.25),
    /* سایه پایین، خیلی محو */ 0 -20px 45px -20px rgba(0, 0, 0, 0.1); /* سایه بالا، محو و ملایم */
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
}

.logo-text {
  font-size: 0.8rem;
  letter-spacing: 1px;
  margin-top: 5px;
}

.cart-button {
  background: #00cdc4;
  color: white;
}
.background-button-login {
  background: linear-gradient(135deg, #00cdc4, #00b8b0);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 3px 5px;
  transition: all 0.25s ease;
}

.background-button-login:hover {
  background: linear-gradient(135deg, #00ddd3, #00b8b0);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 205, 196, 0.38);
}

.background-button-login:active {
  transform: translateY(0);
  box-shadow: 0 4px 10px rgba(0, 205, 196, 0.25);
}

/* آیکون‌ها و دکمه سبد خرید */
.badge-count {
  position: absolute;
  top: -5px;
  right: -8px;
  background: #00cdc4;
  color: white;
  font-size: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-success-light {
  background-color: #dbe4d0;
  cursor: pointer;
}

.cart-icon-bg {
  background: white;
  color: #00cdc4;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-button:hover {
  background: linear-gradient(135deg, #00ddd3, #00b8b0);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 205, 196, 0.38);
}

/* منوی پایین */
.bottom-menu {
  background-color: #00cdc4;

  padding-top: 30px;
}

.nav-link {
  font-size: 0.9rem;
  padding: 5px 10px;
  opacity: 0.9;
}

.nav-link:hover {
  opacity: 1;
  text-decoration: none;
}

.lang-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px; /* فاصله بین دکمه‌ها */

  padding: 5px;
  border-radius: 14px;
  background: #fff8ed;
}

.lang-btn {
  min-width: 42px;
  padding: 6px 11px;

  color: #00cdc4;
  background-color: transparent;
  border: 1.5px solid #00cdc4;
  border-radius: 10px;

  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.4px;

  transition: background-color 0.25s ease, color 0.25s ease, transform 0.2s ease,
    box-shadow 0.25s ease;
}

.lang-btn:hover {
  color: #fff;
  background-color: #00b9b1;
  border-color: #00b9b1;
  transform: translateY(-2px);

  box-shadow: 0 7px 14px rgba(0, 205, 196, 0.28);
}

.lang-btn.active,
.lang-btn.active:hover {
  color: #fff;
  background-color: #00cdc4;
  border-color: #00cdc4;

  box-shadow: 0 5px 12px rgba(0, 205, 196, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.lang-btn:focus-visible {
  outline: 3px solid rgba(0, 205, 196, 0.28);
  outline-offset: 2px;
}

.lang-btn:active {
  transform: translateY(0) scale(0.96);
}

@media (max-width: 576px) {
  .lang-switch {
    gap: 6px;
    padding: 4px;
  }

  .lang-btn {
    min-width: 38px;
    padding: 6px 9px;
  }
}

/* =====================================
   Profile dropdown trigger
===================================== */
.user-menu-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  overflow: visible;
  isolation: isolate;
}

.user-profile-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-width: 64px;
  min-height: 46px;
  padding: 0.35rem 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 15px;
  cursor: pointer;
  background: #00cdc4;
  color: white;
  box-shadow: 0 7px 20px rgba(42, 65, 106, 0.1);
  backdrop-filter: blur(9px);
  transition: all 0.25s ease;
}

.user-profile-trigger:hover,
.user-profile-trigger.active {
  border-color: rgba(111, 125, 248, 0.4);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 25px rgba(88, 103, 210, 0.2);
  transform: translateY(-1px);
}

.user-profile-trigger:focus-visible {
  outline: 3px solid rgba(105, 119, 242, 0.28);
  outline-offset: 3px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  display: flex;
  place-items: center;
  font-size: 1.35rem;
  box-shadow: 0 5px 12px rgba(107, 113, 239, 0.3);
}

.user-chevron {
  color: #6e7c93;
  font-size: 0.72rem;
  transition: transform 0.25s ease;
}

.user-chevron.rotate {
  transform: rotate(180deg);
}

/* =====================================
   Dropdown base
===================================== */
.user-dropdown {
  position: absolute;
  z-index: 9999;
  top: calc(100% + 0.65rem);

  width: 245px;
  max-width: calc(100vw - 20px);
  overflow: hidden;

  border: 1px solid rgba(227, 233, 244, 0.9);
  border-radius: 18px;
  color: #53647e;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 18px 38px rgba(40, 55, 87, 0.16);
  backdrop-filter: blur(12px);
}

/*
  فارسی:
  right: 0 یعنی لبه راست Dropdown با لبه راست دکمه یکی می‌شود.
  پس منو از دکمه به سمت چپ صفحه باز می‌شود و بیرون نمی‌زند.
*/
html[dir="rtl"] .user-dropdown {
  right: 0;
  left: auto;
  text-align: right;
}

/*
  انگلیسی:
  left: 0 یعنی لبه چپ Dropdown با لبه چپ دکمه یکی می‌شود.
  پس منو به سمت راست صفحه باز می‌شود.
*/
html[dir="ltr"] .user-dropdown {
  right: auto;
  left: 0;
  text-align: left;
}

/* =====================================
   User mobile section
===================================== */
.user-mobile-info {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 1rem;
}

.user-mobile-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #8b98ac;
  font-size: 0.76rem;
}

.user-mobile-label i {
  color: #6c7df4;
  font-size: 0.9rem;
}

.user-mobile-info strong {
  color: #394b68;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.4px;
}

/* =====================================
   Dropdown items
===================================== */
.dropdown-line {
  height: 1px;
  margin: 0 0.85rem;
  background: #edf0f5;
}

.user-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 0.85rem 1rem;
  border: 0;
  color: #53647e;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: inherit;
  background: transparent;
  transition: all 0.2s ease;
}

.dropdown-item-content {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.dropdown-item-content > i {
  width: 18px;
  color: #6c7df4;
  font-size: 1rem;
  text-align: center;
}

.item-arrow {
  color: #a6b0c0;
  font-size: 0.7rem;
  transition: transform 0.2s ease;
}

.user-dropdown-item:hover {
  color: #6675e8;
  background: #f5f7ff;
}

/* حرکت فلش مطابق زبان */
html[dir="rtl"] .user-dropdown-item:hover .item-arrow {
  transform: translateX(-3px);
}

html[dir="ltr"] .user-dropdown-item:hover .item-arrow {
  transform: translateX(3px);
}

/* =====================================
   Logout
===================================== */
.logout-item,
.logout-item .dropdown-item-content > i {
  color: #e35968;
}

.logout-item:hover {
  color: #d74759;
  background: #fff3f4;
}

/* =====================================
   Transition
===================================== */
.user-dropdown-enter-active,
.user-dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.user-dropdown-enter-from,
.user-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}

/* =====================================
   Mobile
===================================== */
@media (max-width: 575.98px) {
  .user-profile-trigger {
    min-width: 56px;
    min-height: 43px;
    border-radius: 13px;
  }

  .user-dropdown {
    width: min(245px, calc(100vw - 20px));
    border-radius: 16px;
  }

  .user-dropdown-item {
    min-height: 50px;
  }
}

/* =====================================
   Book categories mega menu
===================================== */
.book-categories-menu {
  position: relative;
}

.categories-chevron {
  margin-right: 4px;
  transition: transform 0.25s ease;
}

.categories-chevron.rotated {
  transform: rotate(180deg);
}

[dir="ltr"] .category-menu-arrow {
  transform: scaleX(-1);
}

.categories-mega-menu {
  position: absolute;
  top: calc(100% + 14px);
  right: auto;
  inset-inline-start: 0;
  z-index: 2000;
  width: min(850px, 92vw);
  overflow: hidden;
  color: #33445c;
  text-align: right;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  box-shadow: 0 20px 45px rgba(23, 50, 75, 0.2);
  backdrop-filter: blur(12px);
}

.categories-mega-menu::before {
  position: absolute;
  top: -7px;
  right: 28px;
  width: 14px;
  height: 14px;
  content: "";
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  border-left: 1px solid rgba(0, 0, 0, 0.04);
  transform: rotate(45deg);
}

.categories-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  min-height: 290px;
}

.categories-column {
  min-width: 0;
  padding: 15px 12px;
  border-left: 1px solid #edf1f4;
}

.categories-column:last-child {
  border-left: 0;
}

.categories-column:nth-child(1) {
  background: linear-gradient(180deg, #e9fbfa 0%, #ffffff 100%);
}

.categories-column:nth-child(3) {
  background: linear-gradient(180deg, #fff8ed 0%, #ffffff 100%);
}

.categories-column-title {
  padding: 0 10px 12px;
  margin-bottom: 6px;
  overflow: hidden;
  color: #18324b;
  font-size: 0.85rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px solid #e8eef0;
}

.category-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px;
  overflow: hidden;
  color: #52647b;
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  text-align: right;
  text-decoration: none;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.category-menu-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-menu-item:hover,
.category-menu-item.active {
  color: #008f89;
  background: #dff8f6;
}

.category-last-level:hover {
  color: #e97828;
  background: #fff0e3;
}

.category-menu-arrow {
  flex-shrink: 0;
  margin-right: 8px;
  font-size: 0.75rem;
}

.category-empty-state {
  padding: 30px 8px;
  margin: 0;
  color: #9ba8b5;
  font-size: 0.78rem;
  text-align: center;
}

.categories-status {
  min-height: 220px;
  padding: 20px;
  font-size: 0.85rem;
}

/* Animation */
.categories-dropdown-enter-active,
.categories-dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.categories-dropdown-enter-from,
.categories-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Mobile / tablet */
@media (max-width: 991.98px) {
  .book-categories-menu {
    width: 100%;
  }

  .categories-mega-menu {
    position: static;
    width: 100%;
    margin-top: 8px;
    border-radius: 14px;
  }

  .categories-mega-menu::before {
    display: none;
  }

  .categories-columns {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .categories-column {
    min-height: auto;
    border-bottom: 1px solid #edf1f4;
    border-left: 0;
  }

  .categories-column:last-child {
    border-bottom: 0;
  }
}

.mobile-header {
  position: relative;
  z-index: 1000;

  width: calc(100% - 24px);
  margin: 12px auto;

  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 18px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  transition: width 0.25s ease, margin 0.25s ease, border-radius 0.25s ease,
    box-shadow 0.25s ease;
}

.mobile-header.is-sticky-mobile {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  transform: none !important; /* جلوی باگ ترنزیشن در RTL رو میگیره */
  border-radius: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.12);
}

/* جلوگیری از پرش محتوا وقتی هدر fixed می‌شود */
.mobile-header-space {
  height: 68px;
}

.mobile-header__content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
}

/* هر دو سمت نوار */
.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;
}

/* لوگو دقیقاً در مرکز نوار می‌ماند */
.mobile-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
}

.mobile-logo img {
  display: block;
  width: auto;
  max-width: 120px;
  height: 60px;
  object-fit: contain;
}

.header-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  color: #00cdc4;
  font-size: 25px;
  text-decoration: none;
  background: transparent;
  border: 0;
  border-radius: 12px;
  transition: 0.2s ease;
}

.header-icon-btn:hover,
.header-icon-btn:focus {
  color: #ffffff;
  background: #00cdc4;
  outline: none;
}

.cart-badge {
  position: absolute;
  top: 2px;
  right: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  color: #fff;
  font-size: 0.68rem;
  line-height: 1;
  background: #dc3545;
  border: 2px solid #fff;
  border-radius: 50px;
}

/* پس‌زمینه هنگام باز بودن سایدبار */
.menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1040;
  background: rgba(0, 0, 0, 0.42);
}

/* پنل منو */
.mobile-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1050;
  width: min(82vw, 330px);
  height: 100vh;
  overflow-y: auto;
  background: #fff;
  box-shadow: -8px 0 25px rgba(0, 0, 0, 0.16);
}

.mobile-sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 70px;
  padding: 0 16px;
  color: #fff;
  background: #00cdc4;
}

.mobile-sidebar__header .header-icon-btn {
  color: #fff;
}

.mobile-sidebar__header .header-icon-btn:hover {
  background: rgba(255, 255, 255, 0.18);
}

.mobile-sidebar__nav {
  padding: 14px 12px;
}

.mobile-sidebar__nav a {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  margin-bottom: 5px;
  color: #18324b;
  font-weight: 500;
  text-decoration: none;
  border-radius: 12px;
  transition: 0.2s ease;
}

.mobile-sidebar__nav a i {
  color: #00cdc4;
  font-size: 1.2rem;
}

.mobile-sidebar__nav a:hover,
.mobile-sidebar__nav a.router-link-active {
  color: #00cdc4;
  background: #f2ecff;
}

/* انیمیشن پس‌زمینه */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* انیمیشن ورود سایدبار از راست */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}

/* =====================================
   Mobile Categories Menu (Accordion)
===================================== */
/* والد کلی */
.mobile-category-item {
  width: 100%;
}

/* آیتم لینک اصلی: همانند سایر لینک‌ها با تراز فضایی */
.mobile-menu-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-decoration: none;
  color: inherit; /* رنگ متن را از منوی شما به ارث می‌برد */
  cursor: pointer;
  background: transparent;
  border: none;
  /* در صورتی که لینک‌های دیگرتان padding مشخصی دارند، همان را اینجا هم ست کنید */
}

/* جهت آیکون و انیمیشن چرخش فلش */
.mobile-arrow {
  transition: transform 0.25s ease;
}

.mobile-arrow.rotated {
  transform: rotate(180deg);
}

/* زیرمنوها فقط یک تورفتگی ظریف دارند بدون اینکه شکل لینک اصلی را خراب کنند */
.mobile-sub-menu {
  overflow: hidden;
  padding-block: 4px;
}

.mobile-tree-item {
  width: 100%;
}

/* عمومی برای دکمه‌های بازشونده داخلی */
.mobile-item-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-family: inherit;
  text-align: start;
  text-decoration: none;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s, color 0.2s;
}

/* سطح ۱ */
.level-1-btn {
  padding: 8px 12px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #475569;
}
.level-1-btn:hover,
.level-1-btn.active {
  color: #008f89;
  background-color: rgba(0, 143, 137, 0.08);
}

/* سطح ۲ */
.mobile-level-2-list {
  padding-inline-start: 12px;
  border-inline-start: 2px solid rgba(0, 143, 137, 0.2);
  margin-inline-start: 16px;
  margin-block: 3px;
}

.level-2-btn {
  padding: 7px 10px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #64748b;
}
.level-2-btn:hover,
.level-2-btn.active {
  color: #008f89;
  background-color: rgba(0, 143, 137, 0.06);
}

/* سطح ۳ (انتخاب نهایی) */
.mobile-level-3-list {
  padding-inline-start: 10px;
  border-inline-start: 2px dashed rgba(233, 120, 40, 0.35);
  margin-inline-start: 14px;
  margin-block: 3px;
}

.level-3-btn {
  padding: 6px 8px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #64748b;
}
.level-3-btn:hover {
  color: #e97828;
  background-color: rgba(233, 120, 40, 0.08);
}

.mobile-action-icon {
  font-size: 0.75rem;
}

/* معکوس کردن فلش در چپ‌چین (LTR) */
[dir="ltr"] .mobile-action-icon {
  transform: scaleX(-1);
}

/* ترنزیشن نرم آکاردئون */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.25s ease;
  max-height: 500px;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>

<style>
/* این تگ بدون scoped است، پس سراسری عمل می‌کند */

/* استانداردسازی ریست و باکس‌سایزینگ */
*,
*::before,
*::after {
  box-sizing: border-box;
}

/* مهار کامل سرریز افقی در ریشه صفحه */
html,
body {
  background-color: #fff8ed !important; /* رنگ مورد نظرتان */

  width: 100%;
  max-width: 100%;
  overflow-x: clip; /* یا hidden */
  margin: 0;
  padding: 0;
}

#app {
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
}

/* جلوگیری از سرریز تصاویر و رسانه‌ها */
img,
picture,
video,
canvas,
svg {
  max-width: 100%;
  height: auto;
  display: block;
}
</style>

